import { useCallback, useMemo, useState } from 'react';
import { DSIcon, DsSelect } from '@eiv2/design-system';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { RequestState } from '../components/RequestState';
import { CollapsibleFiltersCard } from '../components/CollapsibleFiltersCard';
import { DateRangePicker } from '../components/DateRangePicker';
import { StatusChip } from '../components/StatusChip';
import { IconTooltipButton } from '../components/IconTooltipButton';
import { DataGridFrame, visibleDataGridRows } from '../components/DataGridFrame';
import { adminApi } from '../api/client';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { formatDateTime } from '../services/format';
import { getStepLabel } from '../services/labels';
import { getDefaultWindow, type DateRange } from '../services/dateWindow';
import { dataGridLocaleText } from '../theme/dataGridLocale';
import type { CheckoutSummary } from '../types/api';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'active', label: 'Ativo' },
  { value: 'abandoned', label: 'Abandonado' },
  { value: 'converted', label: 'Convertido' }
];

export function CheckoutsPage({
  api = adminApi,
  onOpenCheckout,
  organizer = false
}: {
  api?: Pick<typeof adminApi, 'listCheckouts'>;
  onOpenCheckout?: (row: CheckoutSummary) => void;
  organizer?: boolean;
} = {}) {
  const navigate = useNavigate();
  const [window, setWindow] = useState<DateRange>(() => getDefaultWindow());
  const [status, setStatus] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });

  const fetcher = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.listCheckouts(
        {
          from: window.from,
          to: window.to,
          status: status || undefined,
          limit: 50,
          cursor: cursor ?? undefined
        },
        signal
      ),
    [api, window.from, window.to, status]
  );

  const list = usePaginatedList<CheckoutSummary>(fetcher, [api, window.from, window.to, status]);

  const rows = useMemo(
    () =>
      list.items.map((row) => ({
        ...row,
        id: `${row.organizationId}:${row.source}:${row.sessionId}`
      })),
    [list.items]
  );

  const columns: GridColDef[] = [
    { field: 'organizationId', headerName: 'Organização', width: 140 },
    { field: 'checkoutId', headerName: 'Carrinho', flex: 1, minWidth: 160 },
    { field: 'sessionId', headerName: 'Sessão', flex: 1, minWidth: 160 },
    { field: 'source', headerName: 'Origem', width: 140 },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => <StatusChip status={params.value as string} />,
      sortable: false
    },
    {
      field: 'step',
      headerName: 'Etapa atual',
      flex: 1,
      minWidth: 180,
      valueFormatter: (value: string | null) => getStepLabel(value)
    },
    { field: 'eventCount', headerName: 'Eventos', width: 100, type: 'number' },
    {
      field: 'lastEventAt',
      headerName: 'Última atividade',
      flex: 1,
      minWidth: 200,
      valueFormatter: (value: string) => formatDateTime(value)
    }
  ];

  return (
    <div>
      <PageHeader
        title="Sessões de carrinho"
        subtitle="Sessões de carrinho, progresso e sinais de abandono."
      />

      <CollapsibleFiltersCard
        className="mb-6"
        storageKey="checkouts"
        title="Filtrar por período e status"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <DateRangePicker className="lg:max-w-[520px]" value={window} onChange={setWindow} />
          <div className="min-w-0 w-full flex-1">
            <DsSelect
              onChange={(next) => setStatus(typeof next === 'string' ? next : '')}
              options={STATUS_OPTIONS}
              placeholder="Status"
              value={status}
            />
          </div>
        </div>
      </CollapsibleFiltersCard>

      {list.error ? (
        <RequestState
          title="Não foi possível carregar as sessões"
          error={list.error}
          onRetry={list.reload}
        />
      ) : list.isLoading && rows.length === 0 ? (
        <RequestState title="Carregando sessões" message="Buscando carrinhos recentes." />
      ) : (
        <SectionCard
          action={
            <IconTooltipButton disabled={list.isLoading} label="Atualizar" onClick={list.reload}>
              <DSIcon name="Refresh" size={20} />
            </IconTooltipButton>
          }
          title="Sessões"
          subtitle="Clique em qualquer linha para ver os detalhes"
        >
          <DataGridFrame
            visibleRowCount={visibleDataGridRows(
              rows.length,
              paginationModel.page,
              paginationModel.pageSize
            )}
          >
            <DataGrid
              rows={rows}
              columns={
                organizer
                  ? columns.filter(
                      (column) => !['organizationId', 'sessionId'].includes(column.field)
                    )
                  : columns
              }
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              localeText={{
                ...dataGridLocaleText,
                noRowsLabel: 'Nenhuma sessão encontrada com esses filtros.'
              }}
              onRowClick={(params: GridRowParams) => {
                const row = params.row as CheckoutSummary;
                if (onOpenCheckout) {
                  onOpenCheckout(row);
                  return;
                }
                if (!row.checkoutId) return;
                const search = new URLSearchParams();
                if (row.organizationId) search.set('organizationId', row.organizationId);
                if (row.journeyId) search.set('journeyId', row.journeyId);
                else if (row.source) search.set('source', row.source);
                const query = search.toString();
                navigate(
                  `/checkouts/${encodeURIComponent(row.checkoutId)}${query ? `?${query}` : ''}`
                );
              }}
            />
          </DataGridFrame>
          <LoadMoreButton
            nextCursor={list.nextCursor}
            onLoadMore={list.loadMore}
            isLoading={list.isLoading || list.isLoadingMore}
          />
        </SectionCard>
      )}
    </div>
  );
}
